import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Link } from '@tanstack/react-router';
import { ChevronUp, LogOut, Puzzle, ShieldCheck } from 'lucide-react';
import { ROLE_LABELS_DE, useAuth, type Role } from '@/shared/auth';
import { cn } from '@/shared/lib/cn';
import { visibleAccountEntries } from './navVisibility';

type AccountVariant = 'sidebar' | 'rail' | 'phone';

export function accountIdentity(email: string | undefined, roles: string[] | undefined) {
  const local = (email ?? '?').split('@')[0] ?? '?';
  const initials =
    local
      .replace(/[^a-zA-ZäöüÄÖÜ]/g, '')
      .slice(0, 2)
      .toUpperCase() || '?';
  const role = roles?.find((candidate) => candidate in ROLE_LABELS_DE) as Role | undefined;
  return { initials, roleLabel: role ? ROLE_LABELS_DE[role] : 'Benutzer' };
}

const ICONS: Record<string, typeof ShieldCheck> = {
  '/account/security': ShieldCheck,
  '/plugins': Puzzle,
};
const itemClass =
  'text-ink flex min-h-11 cursor-pointer items-center gap-3 rounded-square px-3 text-sm font-semibold outline-none data-[highlighted]:bg-paper';

/** Konto-Menü: Kontosicherheit und Plugins der eigenen Anmeldung, Abmelden. */
export function AccountMenu({
  variant,
  onLogout,
}: {
  variant: AccountVariant;
  onLogout: () => void;
}) {
  const { user } = useAuth();
  const { initials, roleLabel } = accountIdentity(user?.email, user?.roles);
  const entries = visibleAccountEntries(user);
  const avatar = (dark: boolean, size: string) => (
    <span
      className={cn(
        'rounded-pill flex flex-none items-center justify-center text-xs font-bold',
        dark ? 'bg-ink text-white' : 'text-forest-deep bg-white',
        size,
      )}
    >
      {initials}
    </span>
  );
  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        {variant === 'sidebar' ? (
          <button
            type="button"
            className="border-hairline flex min-h-[54px] w-full items-center gap-2.5 rounded-lg border bg-white px-2.5 py-2 text-left"
          >
            {avatar(true, 'size-9')}
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-ink truncate text-[13px] font-bold">{user?.email ?? '—'}</span>
              <span className="text-ink-soft text-xs">{roleLabel}</span>
            </span>
            <ChevronUp size={14} aria-hidden className="text-ink flex-none" />
          </button>
        ) : (
          <button type="button" aria-label="Konto" title={`${user?.email ?? ''} · ${roleLabel}`}>
            {avatar(variant === 'rail', 'size-11')}
          </button>
        )}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side={variant === 'phone' ? 'bottom' : 'right'}
          align="end"
          sideOffset={12}
          collisionPadding={12}
          className="border-hairline shadow-panel rounded-tile z-50 w-[320px] max-w-[calc(100vw-24px)] overflow-hidden border bg-white"
        >
          <div className="border-hairline flex items-center gap-3 border-b px-[18px] py-4">
            {avatar(true, 'size-11')}
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-[15px] font-bold">{user?.email}</span>
              <span className="text-ink-soft truncate text-xs">
                {roleLabel} · {user?.workspaceName}
              </span>
            </span>
          </div>
          <div className="flex flex-col p-2">
            {entries.map((entry) => {
              const Icon = ICONS[entry.to] ?? ShieldCheck;
              return (
                <DropdownMenu.Item key={entry.to} asChild className={itemClass}>
                  <Link to={entry.to}>
                    <Icon size={18} aria-hidden />
                    {entry.label}
                  </Link>
                </DropdownMenu.Item>
              );
            })}
            {user?.supportSessionId && (
              <DropdownMenu.Item
                disabled
                className={cn(itemClass, 'text-ink-faint cursor-default')}
              >
                <ShieldCheck size={18} aria-hidden />
                <span className="flex flex-col">
                  Kontosicherheit
                  <span className="text-xs font-medium">Gehört zu Ihrer eigenen Anmeldung</span>
                </span>
              </DropdownMenu.Item>
            )}
            <DropdownMenu.Separator className="bg-hairline mx-1 my-1.5 h-px" />
            <DropdownMenu.Item onSelect={onLogout} className={cn(itemClass, 'text-sealwax')}>
              <LogOut size={18} aria-hidden />
              Abmelden
            </DropdownMenu.Item>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
